/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {ActiveElement, ChartDataset, ChartEvent, ChartOptions, ChartType, Plugin, Tick, TooltipItem} from 'chart.js';
import 'chartjs-adapter-moment';
import {PeriodInfoModel} from './models/period-info.model';
import periodsInfoChartConfiguration from './configuration/periods-info-chart-configuration';
import Chart from 'chart.js/auto';
import {PeriodSummaryModel} from '../models/period/period-summary/period-summary.model';
import {PeriodsInfoChartMapper} from './models/mappers/periods-info-chart.mapper';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PeriodsInfoChartStateModel} from './models/periods-info-chart.state.model';
import {BundlesConfigService} from '../services/bundles-config.service';
import {PeriodsViewsService} from '../services/periods-views/periods-views.service';
import {PeriodsInfoChartDatasetMapper} from './mappers/periods-info-chart-dataset.mapper';
import {distinctUntilChanged} from 'rxjs/operators';
import {PeriodsTypeEnum} from '../periods-table/models/periods-type.enum';
import {PeriodMouseActionsEnum} from '../periods-table/models/period-mouse-actions.enum';
import moment from 'moment';

@Component({
    selector: 'app-periods-info-chart',
    templateUrl: './periods-info-chart.component.html',
    styleUrls: ['./periods-info-chart.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PeriodsInfoChartComponent extends StatefulComponent<PeriodsInfoChartStateModel>
        implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
    @ViewChild('periodsInfoChart', {read: ElementRef}) periodsInfoChart: ElementRef;
    @ViewChild('chartWrapper', {read: ElementRef}) chartWrapper: ElementRef;

    @Input() infoChartDataConfiguration: PeriodSummaryModel[];
    @Input() selectedLineIdentifier: string;
    @Input() selectedLineMouseHoverIdentifier: string;
    @Output() openLineIdentifier = new EventEmitter<string>();
    @Output() hoverLineIdentifier = new EventEmitter<string>();

    readonly infoChartTitle = 'Periods total amount over time';
    readonly xAxisLabel = 'Month/Year';
    readonly yAxisLabel = 'Value (¥)';
    private infoChartRef: Chart;
    private previousInfoChartData: PeriodInfoModel[];
    private mouseHoverChartBarEmitter = new EventEmitter<number | null>();

    customAxisTitles: Plugin = {
        id: 'customAxisTitles',
        /**
         * Function that is called after drawing the chart
         * @param chart The created chart
         * @param args The call arguments.
         * @param opts The plugin options.
         */
        afterDraw: (chart: Chart, args: any, opts: any) => {
            const {
                ctx,
                scales: {x, y},
                chartArea: {top, bottom, left, right}
            } = chart;

            if (opts.x.display) {
                ctx.fillStyle = opts.x.color || Chart.defaults.color;
                ctx.font =
                        opts.x.font || '12px "Helvetica Neue", Helvetica, Arial, sans-serif';
                ctx.fillText(
                        opts.x.text,
                        right + (opts.x.offsetX || 0),
                        bottom + (opts.x.offsetY * -1 || 0)
                );
            }

            if (opts.y.display) {
                ctx.fillStyle = opts.y.color || Chart.defaults.color;
                ctx.font =
                        opts.y.font || '12px "Helvetica Neue", Helvetica, Arial, sans-serif';
                ctx.fillText(
                        opts.y.text,
                        opts.y.offsetX || 3,
                        top + (opts.y.offsetY * -1 || -15)
                );
            }
        }
    };

    detectChartMouseLeave: Plugin = {
        id: 'detectChartMouseLeave',
        afterEvent: (chart: Chart, args) => {
            if (args?.event?.type === 'mouseout') {
                this.mouseHoverChartBarEmitter.emit(null);
            }
        }
    };

    constructor(private bundlesConfigService: BundlesConfigService,
                private periodsViewsService: PeriodsViewsService) {
        super();
    }

    get hasNoResultBody(): boolean {
        return this.periodsViewsService.isGlobalSearchCriteriaApplied();
    }

    ngOnInit(): void {
        this.mouseHoverChartBarEmitter.pipe(distinctUntilChanged())
                .subscribe((hoverElementIndex) => {

                    this.resetChartAndTableActionsToDefault();

                    if (hoverElementIndex != null && hoverElementIndex >= 0) {
                        const chartDatasetElement: ChartDataset = this.infoChartRef.config.data.datasets[hoverElementIndex];
                        const hoverBarColor = periodsInfoChartConfiguration.periodMouseActionsColors.get(PeriodMouseActionsEnum.HOVER);
                        this.changeChartDatasetElementColor(chartDatasetElement, hoverBarColor);

                        this.hoverLineIdentifier.emit(this.state.infoChartData[hoverElementIndex].code);
                    }

                    this.infoChartRef.update();
                });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['infoChartDataConfiguration']?.firstChange === false) {
            this.storeState({
                timezone: this.bundlesConfigService.timezone,
                infoChartData: PeriodsInfoChartMapper
                        .fromPeriodSummaryModelArrayToPeriodInfoModelArray(this.infoChartDataConfiguration)
            });
        }
        // Listen for selectedLineIdentifier changes in order to update the chart tooltips to synchronize with the open line action
        if (changes['selectedLineIdentifier']?.firstChange === false &&
                changes['selectedLineIdentifier']?.previousValue !== changes['selectedLineIdentifier']?.currentValue
                || changes['selectedLineMouseHoverIdentifier']?.firstChange === false &&
                changes['selectedLineMouseHoverIdentifier']?.previousValue !== changes['selectedLineMouseHoverIdentifier']?.currentValue) {
            this.resetChartDatasetElementsToDefaultColor();
            this.infoChartRef.update();
        }
    }

    ngAfterViewChecked(): void {
        if (this.state.infoChartData != null && this.previousInfoChartData !== this.state.infoChartData) {
            const context = this.periodsInfoChart.nativeElement.getContext('2d');
            const chartMaxHeight = this.chartWrapper.nativeElement.offsetHeight;
            const chartMaxWidth = this.chartWrapper.nativeElement.offsetWidth;
            this.periodsInfoChart.nativeElement.height = chartMaxHeight;
            this.periodsInfoChart.nativeElement.width = chartMaxWidth;
            this.infoChartRef?.destroy();
            this.infoChartRef = new Chart(context, {
                type: 'line',
                data: {
                    datasets: this.getPeriodsInfoChartDataset(this.state.infoChartData)
                },
                options: this.chartOptions(this.state.timezone),
                plugins: [this.customAxisTitles, this.detectChartMouseLeave]
            });
            this.previousInfoChartData = this.state.infoChartData;
        }
    }

    ngOnDestroy(): void {
        this.infoChartRef?.destroy();
    }

    chartClicked = (elements: ActiveElement[]): void => {
        if (elements != null) {
            this.openLineIdentifier.emit(this.state.infoChartData[elements[0]?.datasetIndex].code);
            this.chartLineClickedHandler(elements[0]?.datasetIndex);
        }
    };

    private chartOptions(timezone: string): ChartOptions {
        return {
            ...periodsInfoChartConfiguration.infoChartOptions,
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        drawTicks: periodsInfoChartConfiguration.gridLineConfiguration.drawTicks,
                        color: periodsInfoChartConfiguration.gridLineConfiguration.color,
                        lineWidth: periodsInfoChartConfiguration.gridLineConfiguration.lineWidth,
                        tickLength: periodsInfoChartConfiguration.gridLineConfiguration.tickLength,
                        tickColor: periodsInfoChartConfiguration.gridLineConfiguration.tickColor,
                        display: periodsInfoChartConfiguration.gridLineConfiguration.display,
                        circular: periodsInfoChartConfiguration.gridLineConfiguration.circular,
                        drawOnChartArea: periodsInfoChartConfiguration.gridLineConfiguration.drawOnChartArea,
                        tickBorderDash: periodsInfoChartConfiguration.gridLineConfiguration.tickBorderDash,
                        tickBorderDashOffset: periodsInfoChartConfiguration.gridLineConfiguration.tickBorderDashOffset,
                        tickWidth: periodsInfoChartConfiguration.gridLineConfiguration.tickWidth,
                        offset: periodsInfoChartConfiguration.gridLineConfiguration.offset,
                        z: periodsInfoChartConfiguration.gridLineConfiguration.z,
                    },
                    ticks: {
                        callback: this.yAxisLabelFormatted,
                        count: periodsInfoChartConfiguration.ticksConfiguration.count
                    }
                },
                x: {
                    type: 'time',
                    time: {
                        unit: 'month',
                        isoWeekday: true,
                        displayFormats: {
                            month: 'MMM YY'
                        }
                    },
                    grid: {
                        drawTicks: periodsInfoChartConfiguration.gridLineConfiguration.drawTicks,
                        color: periodsInfoChartConfiguration.gridLineConfiguration.color,
                        lineWidth: periodsInfoChartConfiguration.gridLineConfiguration.lineWidth,
                        tickLength: periodsInfoChartConfiguration.gridLineConfiguration.tickLength,
                        tickColor: periodsInfoChartConfiguration.gridLineConfiguration.tickColor,
                        display: periodsInfoChartConfiguration.gridLineConfiguration.display,
                        circular: periodsInfoChartConfiguration.gridLineConfiguration.circular,
                        drawOnChartArea: periodsInfoChartConfiguration.gridLineConfiguration.drawOnChartArea,
                        tickBorderDash: periodsInfoChartConfiguration.gridInvisibleLineDash,//the difference with the y axe option
                        tickBorderDashOffset: periodsInfoChartConfiguration.gridLineConfiguration.tickBorderDashOffset,
                        tickWidth: periodsInfoChartConfiguration.gridLineConfiguration.tickWidth,
                        offset: periodsInfoChartConfiguration.gridLineConfiguration.offset,
                        z: periodsInfoChartConfiguration.gridLineConfiguration.z,
                    },
                    ticks: {
                        padding: periodsInfoChartConfiguration.ticksConfiguration.padding
                    }
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip:
                        {
                            callbacks: {
                                title(tooltipItems: TooltipItem<ChartType>[]): string {
                                    return tooltipItems[0].formattedValue + ' ¥';
                                },
                                label(tooltipItem: TooltipItem<ChartType>): string {
                                    const dateData: number[] = tooltipItem.dataset.data.map((val: any) => val?.x);
                                    if (dateData != null) {
                                        const fromDate = moment(dateData[0])
                                                .format('DD-MM-YYYY');
                                        const toDate = moment(dateData[1])
                                                .format('DD-MM-YYYY');
                                        return `From ${fromDate} to ${toDate}`;
                                    }
                                },
                                footer: (tooltipItems: TooltipItem<ChartType>[]): string => this.tooltipFooterText(tooltipItems)
                            },
                            displayColors: false,
                            titleMarginBottom: 2,
                            titleFont: {
                                size: 12,
                                weight: 'normal'
                            },
                            titleColor: 'black',
                            footerColor: 'black',
                            bodyColor: 'black',
                            footerFont: {
                                size: 12,
                                weight: 'normal'
                            },
                            backgroundColor: 'white',
                            cornerRadius: 0,
                            borderColor: 'rgba(174, 174, 174, 0.5)',
                            borderWidth: 1
                        },
                // Typescript is not recognizing custom plugins
                // @ts-ignore
                customAxisTitles: {
                    y: {
                        display: true,
                        text: this.yAxisLabel,
                        offsetX: 48,
                        offsetY: 24
                    },
                    x: {
                        display: true,
                        text: this.xAxisLabel,
                        offsetX: 24,
                        offsetY: 0
                    }
                }
            },
            events: ['mousemove', 'mouseout', 'click'],
            onClick: (event: ChartEvent, elements: ActiveElement[]): void => this.chartClicked(elements),
            onHover: (event: ChartEvent, elements: ActiveElement[]): void => {
                const hoverElementIndex = elements[0]?.datasetIndex;
                this.mouseHoverChartBarEmitter.emit(hoverElementIndex >= 0 ? hoverElementIndex : null);
            }
        };
    }

    private tooltipFooterText = (tooltipItem: TooltipItem<ChartType>[]): string => {
        const tooltipBarIndex = tooltipItem?.[0]?.datasetIndex;
        const barIdentifier = this.state.infoChartData?.[tooltipBarIndex]?.code;
        const lineStatus = this.selectedLineIdentifier != null && this.selectedLineIdentifier === barIdentifier ? 'Retract' : 'Expand';
        return `${lineStatus} period line`;
    };

    private yAxisLabelFormatted(value: number | string, index: number, values: Tick[]): string {
        let formatterValue = value;
        if (typeof value === 'number') {
            const formatter = Intl.NumberFormat('en', {notation: 'compact'} as unknown);
            formatterValue = formatter.format(value);
        }
        return formatterValue?.toString();
    }

    private getPeriodsInfoChartDataset(periods: PeriodInfoModel[]): ChartDataset[] {
        const chartDatasets: ChartDataset[] = [];

        periods?.forEach((period) => {
            const startDateTruncatedByDay = moment(period.timePeriod.startDate)
                    .tz(this.state.timezone)
                    .format('YYYY-MM-DD');
            const endDateFormattedTruncatedByDay = moment(period.timePeriod.endDate)
                    .tz(this.state.timezone)
                    .format('YYYY-MM-DD');

            const startDateTimestamp = moment(startDateTruncatedByDay)
                    .valueOf();
            const endDateTimestamp = moment(endDateFormattedTruncatedByDay)
                    .add(periodsInfoChartConfiguration.barSpacingInHours, 'hours')
                    .valueOf();

            const chartDataset = PeriodsInfoChartDatasetMapper.fromPeriodsInfoModelToChartDataset(
                    period,
                    startDateTimestamp,
                    endDateTimestamp);

            chartDatasets.push(chartDataset);
        });
        return chartDatasets;
    }

    private resetChartDatasetElementsToDefaultColor(): void {
        this.infoChartRef?.config?.data?.datasets?.forEach((elem, index) => {
            // Protection to not reset clicked bar color
            let barColor: string;
            if (this.selectedLineIdentifier === this.state.infoChartData[index].code) {
                barColor = periodsInfoChartConfiguration.periodMouseActionsColors.get(PeriodMouseActionsEnum.CLICK);
            } else if (this.selectedLineMouseHoverIdentifier === this.state.infoChartData[index].code) {
                barColor = periodsInfoChartConfiguration.periodMouseActionsColors.get(PeriodMouseActionsEnum.HOVER);
            } else {
                barColor = periodsInfoChartConfiguration.periodColors
                        .get(<PeriodsTypeEnum> this.state.infoChartData[index].type.code);
            }
            this.changeChartDatasetElementColor(elem, barColor);
        });
    }

    private resetChartAndTableActionsToDefault(): void {
        this.resetChartDatasetElementsToDefaultColor();
        this.hoverLineIdentifier.emit(null);
    }

    private chartLineClickedHandler(chartElementIndex: number): void {
        const chartDatasetElement: ChartDataset = this.infoChartRef.config.data.datasets[chartElementIndex];
        const clickedBarColor = periodsInfoChartConfiguration.periodMouseActionsColors.get(PeriodMouseActionsEnum.CLICK);
        this.changeChartDatasetElementColor(chartDatasetElement, clickedBarColor);
    }

    private changeChartDatasetElementColor(barRef: ChartDataset, color: string): void {
        barRef.backgroundColor = color;
        barRef.borderColor = color;
    }
}
